import { CarouselCard, WhatsAppButton, WhatsAppTemplate, WhatsAppTemplateStatus, WhatsAppTemplateType } from '../../types';

/** What a whatsapp_templates row IS, read off its fields. A row is a shape
 *  (its own body, its own Meta status) or a row that fills a shape
 *  (`parent_id` + `params`, inheriting the parent's status). */

const BLANK = /\{\{(\d+)\}\}/g;

/** The distinct {{n}} blanks in a text, in order. */
export const blanksOf = (text: string | null | undefined): number[] => {
  const seen = new Set<number>();
  for (const m of (text || '').matchAll(BLANK)) seen.add(Number(m[1]));
  return Array.from(seen).sort((a, b) => a - b);
};

export const isFilling = (t: WhatsAppTemplate): boolean => !!t.parent_id;
export const isStaff = (t: WhatsAppTemplate): boolean => t.category === 'UTILITY';

/** A template a sender may send as-is: a filling row, or a shape with no
 *  blank in its body or header. A bare shape with {{n}} fails at Meta with
 *  132000 (proven live), so pickers refuse it. A URL button's {{1}} does not
 *  count — the executor mints the link token itself. */
export const isSelfComplete = (t: WhatsAppTemplate): boolean =>
  isFilling(t) || (blanksOf(t.body).length === 0 && blanksOf(t.header_text).length === 0);

/** Why this template cannot be sent as its approved shape — or null when it
 *  can. Blanks are one half (132000); the other is the component SHAPE, which
 *  Meta rejects with 132012 ("Parameter format does not match format in the
 *  created template"), telling you nothing about which part was wrong. A
 *  carousel step failed this way live on 2026-09-30.
 *
 *  Pass the parent for a filling row, so the shape being checked is the one
 *  Meta approved and the values are the ones this row supplies. The sender
 *  (chatbot's router, `unsendable_reason`) refuses the same shapes — this
 *  keeps them out of a picker so nobody can choose one. */
export const unsendableReason = (t: WhatsAppTemplate, parent?: WhatsAppTemplate): string | null => {
  if (!isSelfComplete(t)) return 'body or header has unfilled blanks';
  const r = render(t, parent);
  if (r.templateType === 'CAROUSEL') {
    if (!r.carouselCards.length) return 'carousel with no cards';
    for (const [i, card] of r.carouselCards.entries()) {
      if (!card.header_media_url) return `card ${i + 1} has no header media`;
      if (blanksOf(card.body).length > (card.body_variables || []).length) {
        return `card ${i + 1} body has unfilled blanks`;
      }
    }
    return null;
  }
  if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(r.templateType) && !r.headerMediaUrl) {
    return `${r.templateType.toLowerCase()} header with no media`;
  }
  return null;
};

/** A template a cadence step (or any sender) may point at. */
export const isSendable = (t: WhatsAppTemplate, parent?: WhatsAppTemplate): boolean =>
  unsendableReason(t, parent) === null;

/** Filling values applied to a shape's text. */
export const fill = (text: string | null | undefined, values: string[]): string =>
  (text || '').replace(BLANK, (m, n) => values[Number(n) - 1] || m);

/** The message as the lead sees it — a filling row rendered through its parent. */
export interface Rendered {
  templateType: WhatsAppTemplateType;
  headerText?: string;
  headerMediaUrl?: string;
  body: string;
  bodyVariables: string[];
  footer?: string;
  buttons: WhatsAppButton[];
  carouselCards: CarouselCard[];
}

export const render = (t: WhatsAppTemplate, parent?: WhatsAppTemplate): Rendered => {
  const shape = t.parent_id ? parent : t;
  if (!shape) {
    // A filling row whose parent is not loaded: nothing to show but its own fields.
    return { templateType: t.template_type, body: '', bodyVariables: [], buttons: [], carouselCards: [] };
  }
  const values = t.parent_id ? (t.params.body_params || []) : (shape.body_variables || []);
  return {
    templateType: shape.template_type,
    headerText: shape.header_text,
    headerMediaUrl: (t.parent_id && t.params.header_media_url) || shape.header_media_url,
    body: shape.body || '',
    bodyVariables: values,
    footer: shape.footer,
    buttons: shape.buttons || [],
    carouselCards: shape.carousel_cards || [],
  };
};

/** The picture a tile shows: the header media, else the first carousel card's. */
export const thumbOf = (r: Rendered): { url: string; video: boolean } | null => {
  if (r.templateType === 'CAROUSEL') {
    const c = r.carouselCards.find((card) => card.header_media_url);
    return c ? { url: c.header_media_url!, video: c.header_format === 'VIDEO' } : null;
  }
  return r.headerMediaUrl ? { url: r.headerMediaUrl, video: r.templateType === 'VIDEO' } : null;
};

/** "video · Book my visit, Send me details" / "carousel · 5 cards". */
export const shapeLabel = (r: Rendered): string => {
  if (r.templateType === 'CAROUSEL') return `carousel · ${r.carouselCards.length} cards`;
  const kind = r.templateType.toLowerCase().replace('_', ' ');
  const labels = r.buttons.map((b) => b.text).filter(Boolean);
  return `${kind} · ${labels.length ? labels.join(', ') : 'no buttons'}`;
};

export const byId = (rows: WhatsAppTemplate[]): Record<string, WhatsAppTemplate> =>
  Object.fromEntries(rows.map((r) => [r.id, r]));

/** The kit Pill tone for a Meta status. */
export const statusTone = (s: WhatsAppTemplateStatus): 'ok' | 'gold' | 'warn' | undefined =>
  s === 'APPROVED' ? 'ok' : s === 'PENDING' ? 'gold' : s === 'REJECTED' ? 'warn' : undefined;
